/**
 * useDashboardIntegrationStatus — F6: Integration Status Utility Hook
 *
 * Shared hook that surfaces the connection status of the three integration
 * categories relevant to the dashboard:
 *
 *   • Finance    — Access Financials / Access Dimensions
 *   • Planning   — Access Planner / Resource Scheduler
 *   • Timesheet  — Access Time / Access FocalPoint / Access Financial Operations
 *
 * Used by widgets throughout the Executive, PM, and Finance dashboards to:
 *   - Gate data display behind integration checks
 *   - Show graceful "Connect your X to unlock this insight" prompt cards
 *   - Display the integration product name badge on widgets
 *   - Drive the planning-tool data waterfall (PRD §2.3)
 *
 * Data source:
 *   Reads from the `integrationsRedesignService` which proxies either the real
 *   backend API (live mode) or the in-memory mock store (demo mode). Keeps in
 *   sync with the same-tab storage-change event broadcast by the service so that
 *   connecting/disconnecting in Settings → Integrations is reflected
 *   immediately on the dashboard without a page reload.
 *
 * Usage:
 *   const { financeStatus, plannerStatus, timesheetStatus } =
 *     useDashboardIntegrationStatus();
 *
 *   if (!financeStatus.isConnected) return <FinancePromptCard />;
 */

import { useState, useEffect, useCallback } from 'react';
import { getCompany } from '@/services/integrationsRedesignService';
import {
  FINANCE_PRODUCT_LABELS,
  PLANNER_PRODUCT_LABELS,
  TIMESHEET_PRODUCT_LABELS,
} from '@/types/integrations-redesign';
import type { IntegrationConnectionStatus } from '@/types/company';
import { useCompany } from '@/contexts/CompanyContext';
import { getDataMode } from '@/lib/data-service';

// ── Per-integration status shape ─────────────────────────────────────────────

export interface IntegrationStatus {
  /** true when status === 'connected' */
  isConnected: boolean;
  /**
   * Raw connection status returned by the service.
   * 'not_configured' = integration not set up at all.
   * 'not_connected'  = configured but currently disconnected.
   * 'error'          = connected previously but now in error state.
   * 'connected'      = healthy and syncing.
   */
  status: IntegrationConnectionStatus;
  /**
   * Human-readable product name (e.g. "Access Financials", "Access Planner").
   * Null when no product is configured — use as the widget badge label.
   */
  productLabel: string | null;
  /** ISO string of the last successful sync, or null if never synced / not configured. */
  lastSyncAt: string | null;
}

// ── Return shape ──────────────────────────────────────────────────────────────

export interface DashboardIntegrationStatusReturn {
  /** Finance system (Access Financials / Access Dimensions). */
  financeStatus: IntegrationStatus;
  /**
   * Planning tool (Access Planner / Resource Scheduler).
   * Drives the planning-tool data waterfall across all dashboards (PRD §2.3).
   */
  plannerStatus: IntegrationStatus;
  /** Timesheet system (Access Time / Access FocalPoint / Access Financial Operations). */
  timesheetStatus: IntegrationStatus;
  /** True while the initial fetch is in-flight. */
  isLoading: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const NOT_CONFIGURED_STATUS: IntegrationStatus = {
  isConnected: false,
  status: 'not_configured',
  productLabel: null,
  lastSyncAt: null,
};

function buildStatus(
  raw: { product: string | null; status: IntegrationConnectionStatus; lastSyncAt: string | null },
  labelMap: Record<string, string>,
): IntegrationStatus {
  const productLabel = raw.product ? (labelMap[raw.product] ?? raw.product) : null;
  return {
    isConnected: raw.status === 'connected',
    status: raw.status,
    productLabel,
    lastSyncAt: raw.lastSyncAt,
  };
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Returns integration statuses for finance, planner, and timesheet.
 * Re-fetches automatically when the active company changes or when the
 * integration storage is updated in the same tab (e.g. from Settings).
 */
export function useDashboardIntegrationStatus(): DashboardIntegrationStatusReturn {
  const { currentCompany } = useCompany();

  const [financeStatus, setFinanceStatus] = useState<IntegrationStatus>(NOT_CONFIGURED_STATUS);
  const [plannerStatus, setPlannerStatus] = useState<IntegrationStatus>(NOT_CONFIGURED_STATUS);
  const [timesheetStatus, setTimesheetStatus] = useState<IntegrationStatus>(NOT_CONFIGURED_STATUS);
  const [isLoading, setIsLoading] = useState(true);

  const loadStatuses = useCallback(async () => {
    // In demo / tour mode assume every integration is connected so all widgets
    // show their full dummy-data content rather than the "Connect your X" prompt.
    if (getDataMode() !== 'live') {
      const now = new Date().toISOString();
      setFinanceStatus({
        isConnected: true,
        status: 'connected',
        productLabel: 'Access Financials',
        lastSyncAt: now,
      });
      setPlannerStatus({
        isConnected: true,
        status: 'connected',
        productLabel: 'Access Planner',
        lastSyncAt: now,
      });
      setTimesheetStatus({
        isConnected: true,
        status: 'connected',
        productLabel: 'Access Time',
        lastSyncAt: now,
      });
      setIsLoading(false);
      return;
    }

    if (!currentCompany) {
      setFinanceStatus(NOT_CONFIGURED_STATUS);
      setPlannerStatus(NOT_CONFIGURED_STATUS);
      setTimesheetStatus(NOT_CONFIGURED_STATUS);
      setIsLoading(false);
      return;
    }

    try {
      const company = await getCompany(currentCompany.id);

      if (!company) {
        // Company not found — treat all integrations as not configured
        setFinanceStatus(NOT_CONFIGURED_STATUS);
        setPlannerStatus(NOT_CONFIGURED_STATUS);
        setTimesheetStatus(NOT_CONFIGURED_STATUS);
        return;
      }

      setFinanceStatus(
        buildStatus(
          company.financeIntegration,
          FINANCE_PRODUCT_LABELS as Record<string, string>,
        ),
      );

      setPlannerStatus(
        buildStatus(
          company.plannerIntegration,
          PLANNER_PRODUCT_LABELS as Record<string, string>,
        ),
      );

      setTimesheetStatus(
        buildStatus(
          company.timesheetIntegration,
          TIMESHEET_PRODUCT_LABELS as Record<string, string>,
        ),
      );
    } catch (err) {
      // Fail gracefully — widgets handle the not-connected state with prompts
      console.error('[useDashboardIntegrationStatus] Failed to fetch integration status:', err);
      setFinanceStatus(NOT_CONFIGURED_STATUS);
      setPlannerStatus(NOT_CONFIGURED_STATUS);
      setTimesheetStatus(NOT_CONFIGURED_STATUS);
    } finally {
      setIsLoading(false);
    }
  }, [currentCompany]);

  // Initial load and company-change reload
  useEffect(() => {
    setIsLoading(true);
    void loadStatuses();
  }, [loadStatuses]);

  // Re-fetch when the integrations store is mutated in the same tab
  // (e.g. user connects/disconnects an integration in Settings → Integrations)
  useEffect(() => {
    const handleUpdate = () => void loadStatuses();

    // integrationsRedesignService broadcasts this event after mutations
    window.addEventListener('integrations-updated', handleUpdate);
    // Catch cross-tab changes via native StorageEvent
    window.addEventListener('storage', handleUpdate);

    return () => {
      window.removeEventListener('integrations-updated', handleUpdate);
      window.removeEventListener('storage', handleUpdate);
    };
  }, [loadStatuses]);

  return { financeStatus, plannerStatus, timesheetStatus, isLoading };
}

// ── Convenience selectors ─────────────────────────────────────────────────────

/**
 * Returns true when the planning tool is connected (either Access Planner or
 * Resource Scheduler). Used to choose between forward-looking booking data
 * and backward-looking timesheet actuals per PRD §2.3.
 *
 * Equivalent to `plannerStatus.isConnected` but available as a named export
 * for use in non-hook utility contexts.
 */
export function isPlannerConnected(plannerStatus: IntegrationStatus): boolean {
  return plannerStatus.isConnected;
}

/**
 * Returns the data source label used by mode-aware widgets.
 *
 * Per PRD §2.3 / §5 (Schedule Health, My Team This Week, Utilisation Snapshot,
 * Headcount & Delivery Capacity):
 *   - Planning tool connected → "Planned" / "Capacity Outlook" mode
 *   - No planning tool         → "Actuals" / "Billable Utilisation" mode
 */
export function getPlannerDataSourceLabel(
  plannerStatus: IntegrationStatus,
): 'planning-tool' | 'timesheets' {
  return plannerStatus.isConnected ? 'planning-tool' : 'timesheets';
}
